const BASE_URL = 'https://prod.wp.usefulchaos.com';
const API_KEY = 'hf0m2H38Xx4Cf6rnbT98NazbVo4bE5Vu2k4hqtyj';
const ENGINE = 'rapidapi';
const REGION = 'us-west-2';
const SERVICE = 'execute-api';
const IDENTITY_POOL_ID = 'us-west-2:95f73c2d-7cdb-4d36-b84b-83f97e765db9';
const COGNITO_ENDPOINT = `https://cognito-identity.${REGION}.amazonaws.com`;

let cachedCredentials = null;

// --- SigV4 signing using Web Crypto API ---

const encoder = new TextEncoder();

async function hmac(key, data) {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    typeof key === 'string' ? encoder.encode(key) : key,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  return new Uint8Array(await crypto.subtle.sign('HMAC', cryptoKey, encoder.encode(data)));
}

async function sha256(data) {
  const hash = await crypto.subtle.digest('SHA-256', encoder.encode(data));
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
}

function toHex(buffer) {
  return Array.from(new Uint8Array(buffer)).map(b => b.toString(16).padStart(2, '0')).join('');
}

async function signRequest(method, url, headers, body, credentials) {
  const now = new Date();
  const amzDate = now.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const dateStamp = amzDate.slice(0, 8);

  headers['x-amz-date'] = amzDate;
  if (credentials.SessionToken) {
    headers['x-amz-security-token'] = credentials.SessionToken;
  }

  const payloadHash = await sha256(body || '');
  const parsedUrl = new URL(url);

  const signedHeaderKeys = Object.keys(headers)
    .map(k => k.toLowerCase())
    .concat('host')
    .sort();
  const signedHeaders = signedHeaderKeys.join(';');

  const canonicalHeaders = signedHeaderKeys
    .map(k => k === 'host' ? `host:${parsedUrl.hostname}\n` : `${k}:${headers[Object.keys(headers).find(h => h.toLowerCase() === k)]}\n`)
    .join('');

  const canonicalRequest = [
    method,
    parsedUrl.pathname,
    '',
    canonicalHeaders,
    signedHeaders,
    payloadHash,
  ].join('\n');

  const credentialScope = `${dateStamp}/${REGION}/${SERVICE}/aws4_request`;

  const stringToSign = [
    'AWS4-HMAC-SHA256',
    amzDate,
    credentialScope,
    await sha256(canonicalRequest),
  ].join('\n');

  const signingKey = await hmac(
    await hmac(
      await hmac(
        await hmac(`AWS4${credentials.SecretKey}`, dateStamp),
        REGION
      ),
      SERVICE
    ),
    'aws4_request'
  );

  const signature = toHex(await crypto.subtle.sign(
    'HMAC',
    await crypto.subtle.importKey('raw', signingKey, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']),
    encoder.encode(stringToSign)
  ));

  headers['Authorization'] = `AWS4-HMAC-SHA256 Credential=${credentials.AccessKeyId}/${credentialScope}, SignedHeaders=${signedHeaders}, Signature=${signature}`;

  return headers;
}

// --- Cognito credentials ---

async function cognitoRequest(action, body) {
  const response = await fetch(COGNITO_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityService.${action}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`Cognito ${action} failed: ${response.status}`);
  }

  return response.json();
}

async function getCredentials() {
  if (cachedCredentials && new Date(cachedCredentials.Expiration * 1000) > new Date()) {
    return cachedCredentials;
  }

  const identity = await cognitoRequest('GetId', { IdentityPoolId: IDENTITY_POOL_ID });
  const result = await cognitoRequest('GetCredentialsForIdentity', { IdentityId: identity.IdentityId });
  cachedCredentials = result.Credentials;
  return cachedCredentials;
}

// --- API client ---

async function post(path, body = {}) {
  const payload = JSON.stringify({ engine: ENGINE, ...body });
  const url = `${BASE_URL}${path}`;
  const credentials = await getCredentials();

  const headers = {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
  };

  await signRequest('POST', url, headers, payload, credentials);

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: payload,
  });

  if (!response.ok) {
    const text = await response.text();
    let message;
    try {
      const err = JSON.parse(text);
      message = err.error || err.message || text;
    } catch {
      message = text;
    }
    throw new Error(message);
  }

  return response.json();
}

export function fetchSeasons() {
  return post('/seasons');
}

export function fetchWeeks(year) {
  return post(`/seasons/${year}/weeks`);
}

export function fetchGames(year, week) {
  return post(`/seasons/${year}/weeks/${week}/games`);
}

export function fetchPlays(gameId) {
  return post(`/games/${gameId}/plays`);
}
